import { InfoCircleOutlined } from '@ant-design/icons';
import type { RadioChangeEvent } from 'antd';
import {
  Button,
  Checkbox,
  Form,
  InputNumber,
  Modal,
  Radio,
  Tooltip,
} from 'antd';
import classNames from 'classnames';
import { useContext, useEffect, useRef, useState } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import type { OpeningConfigurationType } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { ExperienceType } from 'app/typings/experiences';

import {
  markFormDirty,
  useValidationErrors,
} from '../../hook/useValidationStore';

import './Availability.scss';
import { StockConfigurationTable } from './_components/StockConfigurationTable';

const RULES = [{ required: true, message: 'Required' }];

export const StockConfiguration = () => {
  const { isViewMode, isEditMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance();
  const { stockError } = useValidationErrors();

  const [showModal, setShowModal] = useState(false);
  const [pendingIsUnlimited, setPendingIsUnlimited] = useState<boolean | null>(
    null
  );
  const [pendingHideInventory, setPendingHideInventory] = useState(false);

  const experienceType = Form.useWatch(['experienceType'], form);
  const isOriginal = experienceType === ExperienceType.STAYCATION;
  const openingConfiguration = Form.useWatch(['openingConfiguration'], form);
  const isAlwaysIncludedWithNight = Form.useWatch(
    ['isAlwaysIncludedWithNight'],
    form
  );
  const showInInventory = Form.useWatch('showInInventory', form);
  const isHiddenFromInventory = showInInventory === false;

  const [isUnlimited, setIsUnlimited] = useState<boolean | null>(null);
  const isUnlimitedInitialized = useRef(false);

  useEffect(() => {
    if (isUnlimitedInitialized.current) {
      return;
    }

    const stockConfig = openingConfiguration?.stockConfiguration;

    if (
      !stockConfig ||
      stockConfig.type !== OPENING_CONFIGURATION_TYPE.CONSTANT
    ) {
      return;
    }

    const stock = stockConfig.configuration?.stock;

    if (stock === null) {
      setIsUnlimited(true);
      isUnlimitedInitialized.current = true;
    } else if (typeof stock === 'number') {
      setIsUnlimited(false);
      isUnlimitedInitialized.current = true;
    }
  }, [openingConfiguration?.stockConfiguration]);

  const hasStockConfiguration = isUnlimited === false;
  const isUnlimitedStock = isUnlimited === true || isOriginal;
  const configurationType: OpeningConfigurationType =
    openingConfiguration?.stockConfiguration?.type;
  const hasExistingStockConfig = isUnlimited !== null || isOriginal;

  const handleUnlimitedCheckboxChange = () => {
    const newIsUnlimited = !isUnlimitedStock;

    if (isUnlimited !== null) {
      setPendingIsUnlimited(newIsUnlimited);
      setShowModal(true);
    } else {
      markFormDirty();
      setIsUnlimited(newIsUnlimited);
      form.setFieldsValue({
        openingConfiguration: {
          stockConfiguration: {
            configuration: { stock: newIsUnlimited ? null : 0 },
          },
        },
      });
    }
  };

  const handleModalConfirm = () => {
    markFormDirty();

    if (pendingHideInventory) {
      setIsUnlimited(true);
      form.setFieldsValue({
        showInInventory: false,
        openingConfiguration: {
          stockConfiguration: {
            type: OPENING_CONFIGURATION_TYPE.CONSTANT,
            configuration: { stock: null },
          },
        },
      });
    } else {
      setIsUnlimited(pendingIsUnlimited);
      form.setFieldsValue({
        openingConfiguration: {
          stockConfiguration: {
            configuration: { stock: pendingIsUnlimited ? null : 0 },
          },
        },
      });
    }

    setShowModal(false);
    setPendingIsUnlimited(null);
    setPendingHideInventory(false);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setPendingIsUnlimited(null);
    setPendingHideInventory(false);
  };

  const handleShowInInventoryChange = (e: RadioChangeEvent) => {
    const newValue = e.target.value;

    if (newValue === false && hasExistingStockConfig) {
      form.setFieldValue('showInInventory', true);
      setPendingHideInventory(true);
      setShowModal(true);

      return;
    }

    if (newValue === false) {
      setIsUnlimited(true);
      form.setFieldsValue({
        openingConfiguration: {
          stockConfiguration: {
            type: OPENING_CONFIGURATION_TYPE.CONSTANT,
            configuration: { stock: null },
          },
        },
      });
    }

    markFormDirty();
  };

  return (
    <SCard
      title="Stock"
      subtitle="Control activity visibility in the hotel inventory and set default stock rules (editable in the Extranet)."
      className="experience-form stock-configuration"
    >
      <FormLineLayout
        isRequired
        label={
          <div>
            Show in Inventory{' '}
            <Tooltip title="Select “No” if this activity doesn’t require its own inventory. This keeps the hotel’s Extranet inventory clean and easy to manage (e.g. coffee, pancakes, estate access).">
              <InfoCircleOutlined />
            </Tooltip>
          </div>
        }
        labelOnTop
        disabled={isViewMode}
      >
        <Form.Item name="showInInventory" rules={RULES} initialValue={true}>
          <Radio.Group
            disabled={isViewMode}
            onChange={handleShowInInventoryChange}
          >
            <Radio value={true}>
              Yes{' '}
              <span
                className={classNames(!isViewMode ? 'radio-helper-text' : '')}
              >
                - Managed by the hotel in the Extranet Inventory
              </span>
            </Radio>
            <Radio value={false}>
              No{' '}
              <span
                className={classNames(!isViewMode ? 'radio-helper-text' : '')}
              >
                - Hidden from the hotel inventory (no stock required)
              </span>
            </Radio>
          </Radio.Group>
        </Form.Item>
      </FormLineLayout>

      <FormLineLayout
        isRequired
        label="Configuration"
        labelOnTop
        disabled={isViewMode}
      >
        <div className="stock-configuration-wrapper">
          <Form.Item
            name={['openingConfiguration', 'stockConfiguration', 'type']}
            rules={RULES}
            initialValue={OPENING_CONFIGURATION_TYPE.CONSTANT}
          >
            <Radio.Group disabled={isViewMode}>
              <Radio
                value={OPENING_CONFIGURATION_TYPE.CONSTANT}
                disabled={isHiddenFromInventory}
              >
                Same every day
              </Radio>
              <Radio
                value={OPENING_CONFIGURATION_TYPE.DAILY}
                disabled={isHiddenFromInventory}
              >
                Depends on the day
              </Radio>
            </Radio.Group>
          </Form.Item>
          {configurationType === OPENING_CONFIGURATION_TYPE.CONSTANT && (
            <>
              <div className="stock-configuration-unlimited-constant">
                <span className="stock-configuration-unlimited-constant-label">
                  Stock:
                </span>
                {isAlwaysIncludedWithNight ? (
                  <span>Publish unlimited (∞) stock everyday</span>
                ) : (
                  <Checkbox
                    checked={isUnlimitedStock}
                    onChange={handleUnlimitedCheckboxChange}
                    disabled={isViewMode || isOriginal || isHiddenFromInventory}
                  >
                    Unlimited (∞)
                  </Checkbox>
                )}
              </div>
              {!isAlwaysIncludedWithNight && (
                <>
                  <div className="stock-configuration-constant">
                    <span>Publish</span>
                    <Form.Item
                      name={[
                        'openingConfiguration',
                        'stockConfiguration',
                        'configuration',
                        'stock',
                      ]}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (isUnlimitedStock) {
                              return Promise.resolve();
                            }

                            if (value === undefined || value === null) {
                              return Promise.reject(new Error('Required'));
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      {isUnlimitedStock ? (
                        <InputNumber disabled placeholder="∞" />
                      ) : isEditMode ? (
                        <InputNumber
                          type="number"
                          min={0}
                          disabled={isHiddenFromInventory}
                        />
                      ) : (
                        <span className="view-data">
                          {openingConfiguration?.stockConfiguration
                            ?.configuration?.stock || 'ø empty'}
                        </span>
                      )}
                    </Form.Item>
                    <span>stock every day</span>
                  </div>
                  {isEditMode &&
                    hasStockConfiguration &&
                    !stockError.isValid && (
                      <span className="form-error-message">
                        {stockError.errors.map((error, index) => (
                          <div key={index}>- {error.message}</div>
                        ))}
                      </span>
                    )}
                </>
              )}
            </>
          )}
        </div>
      </FormLineLayout>
      {configurationType === OPENING_CONFIGURATION_TYPE.DAILY && (
        <FormLineLayout label="Stock" labelOnTop disabled={isViewMode}>
          <StockConfigurationTable />
        </FormLineLayout>
      )}

      <Modal
        open={showModal}
        onCancel={handleModalCancel}
        footer={
          <>
            <Button onClick={handleModalCancel}>Cancel</Button>
            <Button type="primary" onClick={handleModalConfirm}>
              Yes, save changes
            </Button>
          </>
        }
        closable
        title="Override all manual stock"
      >
        This action will delete all manual stock data entered by the hotel for
        this experience. Are you sure you want to proceed?
      </Modal>
    </SCard>
  );
};
